import { Form } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useStore } from 'store/useStore'
import { getCreateOrderPageKey } from 'sections/LKSection/modules/OrderModule/utils'
import { pickPointDocumentForEditAreaItems } from 'sections/LKSection/shared/PickPointSharedModule/components/PickPointFilesForm/data'
import { PickPointFileItem } from 'sections/LKSection/shared/PickPointSharedModule/components/PickPointFilesForm/PickPointFileItem/PickPointFileItem'
import { Info } from 'components/ui/Info/Info'
import { Button, Divider, Input } from 'components/uiKit'
import { getSharedKey } from 'utils/helpers/i18n/getKeys'
import { LKRoutesEnum } from 'shared/constants/routes'

import { PvzKeys } from '../../../data'
import { EditAreaStatus } from '../../../types/types'
import { validatePvzEditArea } from './validate'

type PvzEditAreaType = {
  area: number
  warehouseArea: number
}

const getKey = (key: string) => `${PvzKeys.editAreaPage}.${key}`

export const PvzEditAreaForm = observer(() => {
  const { viewPvzUiStore: uiStore } = useStore()

  const navigate = useNavigate()
  const [form] = Form.useForm()
  const { t } = useTranslation()
  const [loadingUpdate, setLoadingUpdate] = useState(false)

  const pvz = uiStore.selectedPvz
  const areaModel = uiStore.areaModel

  const isOwner = uiStore.selectedPvz?.id ? uiStore.isOwner(uiStore.selectedPvz.id) : false
  const disabled = !isOwner || Boolean(pvz?.blockTime)
  const isEditAreaStatusInProgress = pvz?.editAreaStatus === EditAreaStatus.inProgress

  useEffect(() => {
    if (areaModel.initialized && pvz) {
      form.setFieldValue('area', pvz.area)
      form.setFieldValue('warehouseArea', pvz.warehouseArea)
    }
  }, [areaModel.initialized, pvz, form])

  const updateButtonHandler = async () => {
    if (validatePvzEditArea.validate(areaModel) && pvz?.id) {
      setLoadingUpdate(true)

      if (await uiStore.updatePvz(pvz.id)) {
        navigate(LKRoutesEnum.pvz)
      }

      setLoadingUpdate(false)
    }
  }

  return (
    <Form layout="vertical" form={form} requiredMark={false}>
      <Info>
        {isEditAreaStatusInProgress
          ? t(getKey('Вы уже подали заявку на смену площади ПВЗ'))
          : t(getKey('При изменении площади ПВЗ'))}
      </Info>

      <Form.Item<PvzEditAreaType>
        label={t(getKey('Площадь ПВЗ'))}
        rules={[{ required: true, message: t(getSharedKey('required')) }]}
        name={'area'}
      >
        <Input
          value={areaModel.area}
          onChange={(e) => areaModel.setArea(e.target.value)}
          disabled={disabled || isEditAreaStatusInProgress}
          placeholder="50"
          type="number"
        />
      </Form.Item>

      <Form.Item<PvzEditAreaType>
        label={t(getCreateOrderPageKey('workConditionsStep.Площадь склада'))}
        rules={[{ required: true, message: t(getSharedKey('required')) }]}
        name={'warehouseArea'}
      >
        <Input
          value={areaModel.warehouseArea}
          onChange={(e) => areaModel.setWarehouseArea(e.target.value)}
          disabled={disabled}
          placeholder="30"
          type="number"
        />
      </Form.Item>

      <Divider />

      {pickPointDocumentForEditAreaItems.map((documentItem) => (
        <Form.Item key={documentItem.fileType}>
          <PickPointFileItem
            disabled={disabled || !areaModel.isAreaWasChanged || isEditAreaStatusInProgress}
            uiStore={uiStore.fileList}
            fileType={documentItem.fileType}
            label={documentItem.label}
            description={documentItem.description}
            dataTestId={documentItem.dataTestId}
            initialized={uiStore.initialized}
          />
        </Form.Item>
      ))}

      <Button
        htmlType="submit"
        disabled={areaModel.isNoChanges || uiStore.isSwitchPvzLoading}
        loading={loadingUpdate || uiStore.isSwitchPvzLoading}
        onClick={updateButtonHandler}
      >
        {t(getKey('Обновить ПВЗ'))}
      </Button>
    </Form>
  )
})
